import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, X, Send, Loader2, User, Bot, 
  Minimize2, Maximize2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const loadChatHistory = useCallback(async (sid) => {
    try {
      const response = await axios.get(`${API}/chat/history/${sid}`);
      if (response.data.messages && response.data.messages.length > 0) {
        setMessages(response.data.messages.map(msg => ({
          id: `${msg.role}-${msg.timestamp || Date.now()}-${Math.random()}`,
          role: msg.role,
          content: msg.content
        })));
      } else {
        setMessages([{
          id: 'welcome-msg',
          role: 'assistant',
          content: "Hello! I'm the Elementary Cargo assistant. I can help you with questions about our logistics services, shipping options, or guide you to request a quote. How can I assist you today?"
        }]);
      }
    } catch {
      setMessages([{
        id: 'welcome-msg',
        role: 'assistant',
        content: "Hello! I'm the Elementary Cargo assistant. I can help you with questions about our logistics services, shipping options, or guide you to request a quote. How can I assist you today?"
      }]);
    }
  }, []);

  // Generate or retrieve session ID
  useEffect(() => {
    let storedSessionId = localStorage.getItem('chat_session_id');
    if (!storedSessionId) {
      storedSessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chat_session_id', storedSessionId);
    }
    setSessionId(storedSessionId);
    loadChatHistory(storedSessionId);
  }, [loadChatHistory]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    const msgId = `user-${Date.now()}`;
    setInputValue('');
    
    setMessages(prev => [...prev, { id: msgId, role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat/message`, {
        message: userMessage,
        session_id: sessionId
      });

      setMessages(prev => [...prev, { 
        id: `assistant-${Date.now()}`,
        role: 'assistant', 
        content: response.data.response 
      }]);
    } catch {
      setMessages(prev => [...prev, { 
        id: `error-${Date.now()}`,
        role: 'assistant', 
        content: "I apologize, but I'm having trouble responding right now. Please try again or contact us directly at info@elementarycargo.com or +31 62 538 1824." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    const newSessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('chat_session_id', newSessionId);
    setSessionId(newSessionId);
    setMessages([{
      id: 'welcome-msg',
      role: 'assistant',
      content: "Hello! I'm the Elementary Cargo assistant. I can help you with questions about our logistics services, shipping options, or guide you to request a quote. How can I assist you today?"
    }]);
  };

  // Quick action buttons
  const quickActions = [
    { label: 'Services', message: 'What services do you offer?' },
    { label: 'Get Quote', message: 'I want to request a quote' },
    { label: 'Contact', message: 'How can I contact your team?' }
  ];

  return (
    <>
      {/* Chat Toggle Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-[10000] w-16 h-16 bg-electric hover:bg-electric/90 text-white rounded-full shadow-xl hover:shadow-2xl transition-all flex items-center justify-center group"
            data-testid="chat-widget-toggle"
          >
            <MessageCircle className="w-7 h-7 group-hover:scale-110 transition-transform" />
            {/* Notification dot */}
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber rounded-full border-2 border-white" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              height: isMinimized ? 'auto' : '520px'
            }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-[10000] w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
            data-testid="chat-widget-window"
          >
            {/* Header */}
            <div className="bg-slate-900 px-4 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan/20 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-cyan" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Elementary Cargo</h3>
                  <p className="text-slate-400 text-xs">AI Assistant</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                >
                  {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                  data-testid="chat-close-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Chat Content (hidden when minimized) */}
            {!isMinimized && (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50" data-testid="chat-messages">
                  {messages.map((msg) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="w-7 h-7 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-cyan" />
                        </div>
                      )}
                      <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                        msg.role === 'user' 
                          ? 'bg-electric text-white rounded-br-sm' 
                          : 'bg-white text-slate-700 border border-slate-200 rounded-bl-sm shadow-sm'
                      }`}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-7 h-7 bg-electric rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </motion.div>
                  ))}
                  
                  {/* Loading indicator */}
                  {isLoading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex gap-2 items-start"
                    >
                      <div className="w-7 h-7 bg-slate-900 rounded-full flex items-center justify-center">
                        <Bot className="w-4 h-4 text-cyan" />
                      </div>
                      <div className="bg-white p-3 rounded-2xl rounded-bl-sm border border-slate-200 shadow-sm">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Quick Actions (show only if few messages) */}
                {messages.length <= 2 && !isLoading && (
                  <div className="px-4 pb-2 flex flex-wrap gap-2">
                    {quickActions.map((action) => (
                      <button
                        key={action.label}
                        onClick={() => {
                          setInputValue(action.message);
                          setTimeout(() => sendMessage(), 100);
                        }}
                        className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full transition-colors"
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Input */}
                <div className="p-3 border-t border-slate-200 bg-white flex-shrink-0">
                  <div className="flex gap-2">
                    <Input
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      className="flex-1 h-10 text-sm border-slate-200 focus:border-electric focus:ring-electric/20 rounded-xl"
                      disabled={isLoading}
                      data-testid="chat-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={!inputValue.trim() || isLoading}
                      className="h-10 w-10 p-0 bg-electric hover:bg-electric/90 rounded-xl"
                      data-testid="chat-send-btn"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  
                  {/* Contact fallback */}
                  <div className="mt-2 text-center">
                    <p className="text-xs text-slate-400">
                      Or contact us:{' '}
                      <a href="mailto:info@elementarycargo.com" className="text-electric hover:underline">
                        info@elementarycargo.com
                      </a>
                    </p>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
